import { useState } from "react";
import { toast } from "sonner";
import { FileSearch, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { errText } from "@/components/v52/ImportParts";
import { useT52 } from "@/i18n/v52";

const tone = (s) => (!s ? "unk" : s.startsWith("blocked") ? "fail" : "ok");

function LinkChip({ label, s, testid }) {
  const { t52 } = useT52();
  if (!s) return null;
  const k = s.startsWith("blocked") ? "blocked" : s;
  return (
    <span className={`f52-chip ${tone(s)}`} data-testid={testid}>
      {label}: {t52(`ops_sp_ln_${k}`)}{k === "blocked" ? ` (${s.split(":")[1]})` : ""}
    </span>
  );
}

function LocRow({ x }) {
  const { t52 } = useT52();
  const id = `ops-sp-loc-${x.ref.replace(/[^a-zA-Z0-9-]/g, "-")}`;
  return (
    <li data-testid={id}>
      <span><b>{x.name}</b> · {x.street}, {x.postal_code} {x.city} ({x.canton})</span>
      <small className="f52-mono">{x.ref}{x.location_id ? ` → ${x.location_id}` : ""}</small>
      <div className="flex flex-wrap gap-2">
        <span className={`f52-chip ${tone(x.status)}`} data-testid={`${id}-status`}>{t52(`ops_sp_st_${x.status}`)}</span>
        <LinkChip label="A–Z" s={x.az} testid={`${id}-az`} />
        <LinkChip label="Sales" s={x.sales} testid={`${id}-sales`} />
        <LinkChip label="Research" s={x.research} testid={`${id}-research`} />
      </div>
      {x.reasons.map((r) => <small key={r} className="f52-imp-warn">{r}</small>)}
    </li>
  );
}

function OpBlock({ op }) {
  const { t52 } = useT52();
  return (
    <div className="f52-imp-body" data-testid={`ops-sp-op-${op.key}`}>
      <h3 className="flex flex-wrap items-center gap-2 font-semibold">
        {op.company_name}
        <span className={`f52-chip ${tone(op.status)}`} data-testid={`ops-sp-op-${op.key}-status`}>{t52(`ops_sp_st_${op.status}`)}</span>
        <small className="f52-mono">{op.source_ref.join(" / ")}{op.tenant_id ? ` → ${op.tenant_id}` : ""}</small>
      </h3>
      {op.reasons.map((r) => <p key={r} className="f52-imp-warn">{r}</p>)}
      <ul className="f52-ops-list">{op.locations.map((x) => <LocRow key={x.ref} x={x} />)}</ul>
    </div>
  );
}

function PreviewBody({ pv }) {
  const { t52, tOr } = useT52();
  const b = pv.totals.before;
  const a = pv.totals.expected_after;
  const c = pv.counts;
  return (
    <div className="f52-imp-body" data-testid="ops-sp-preview">
      <p data-testid="ops-sp-totals">{t52("ops_sp_totals", { o: b.real_orgs, l: b.real_locations, eo: a.real_orgs, el: a.real_locations })}</p>
      <p data-testid="ops-sp-counts">{t52("ops_sp_counts", { on: c.orgs_new, ln: c.locations_new, oe: c.orgs_existing, le: c.locations_existing, k: c.links_open, b: c.blocked })}</p>
      {pv.ready ? <p className="f52-chip ok" data-testid="ops-sp-ready">{t52("ops_sp_ready")}</p> : (
        <div className="f52-imp-warn" data-testid="ops-sp-problems">
          <b>{t52("ops_sp_problems")}:</b> {pv.problems.map((p) => tOr(`ops_sp_pb_${p}`, p)).join(" · ")}
          {[...(pv.deviations || []), ...(pv.drift || [])].slice(0, 8).map((d) => <small key={d} className="block">{d}</small>)}
        </div>
      )}
      {pv.operators.map((op) => <OpBlock key={op.key} op={op} />)}
    </div>
  );
}

export function SupplementPanel({ info, busy, canStart, onStart }) {
  const { t52, tOr } = useT52();
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(false);
  if (!info) return null;
  const load = async () => {
    setLoading(true);
    try { setPv((await api.get("/import-ops/supplement/preview")).data); } catch (e) { toast.error(errText(t52, tOr, e)); }
    setLoading(false);
  };
  const actions = info.ok && (
    <>
      <button type="button" onClick={load} disabled={loading} data-testid="ops-sp-preview-btn" className="f52-btn f52-btn-secondary">
        {loading ? <Loader2 aria-hidden="true" className="f52-spin" /> : <FileSearch aria-hidden="true" />}{t52("ops_sp_preview")}
      </button>
      <button type="button" onClick={onStart} disabled={!pv?.ready || busy || !canStart} data-testid="ops-sp-start-btn" className="f52-btn f52-btn-danger-solid">
        <ShieldCheck aria-hidden="true" />{t52("ops_sp_start")}
      </button>
    </>
  );
  return (
    <Panel title={t52("ops_sp_t")} subtitle={info.ok ? t52("ops_sp_lead", { id: info.id, o: info.expect.orgs, l: info.expect.locations }) : null} actions={actions} testid="ops-supplement-panel">
      {!info.ok ? <p className="f52-imp-warn" data-testid="ops-sp-invalid">{t52("ops_sp_invalid", { e: info.error })}</p> : (
        <div className="f52-imp-body">
          <p className="f52-imp-muted f52-mono" data-testid="ops-sp-plan-sha">{info.title} · SHA256 {info.sha256.slice(0, 16)}… · {info.evidence.path}</p>
          <p className="f52-imp-muted" data-testid="ops-sp-excluded"><b>{t52("ops_sp_excluded")}:</b> {info.excluded.map((x) => x.name).join(" · ")}</p>
          {pv ? <PreviewBody pv={pv} /> : <p className="f52-imp-muted" data-testid="ops-sp-none">{t52("ops_sp_none")}</p>}
        </div>
      )}
    </Panel>
  );
}
